import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import {
  Bell,
  Check,
  ChevronDown,
  Globe,
  Maximize2,
  Menu as MenuIcon,
  Minimize2,
  Moon,
  Sun,
  User as UserIcon,
  LogOut,
  Monitor,
  type LucideIcon,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import Avatar from '@/components/ui/Avatar';
import { TSystemsMark } from '@/components/brand/TSystemsMark';
import { useAuthUser, useLogout } from '@/store/auth';

type HeaderProps = {
  onToggleSidebar?: () => void;
};

type ThemeMode = 'light' | 'dark' | 'system';

const Header: React.FC<HeaderProps> = ({ onToggleSidebar }) => {
  const { t } = useTranslation('common');
  const user = useAuthUser();
  const logout = useLogout();

  const [fullscreen, setFullscreen] = useState(false);
  const [theme, setTheme] = useState<ThemeMode>(() => {
    if (typeof window === 'undefined') return 'system';
    return (document.documentElement.dataset.theme as ThemeMode) || 'system';
  });
  const [locale, setLocale] = useState<string>(
    () => (typeof document !== 'undefined' && document.documentElement.lang) || 'pt-BR',
  );

  // Resolve theme -> apply to <html>.
  useEffect(() => {
    const root = document.documentElement;
    const wantsDark =
      theme === 'dark' ||
      (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    root.classList.toggle('dark', wantsDark);
    root.dataset.theme = theme;
  }, [theme]);

  // Track fullscreen state changes from any source (Esc, browser).
  useEffect(() => {
    const onChange = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    if (fullscreen) {
      await document.exitFullscreen().catch(() => {});
      return;
    }
    await document.documentElement.requestFullscreen().catch(() => {});
  }, [fullscreen]);

  const onChangeLocale = (next: string) => {
    setLocale(next);
    document.documentElement.lang = next;
    // real i18n.changeLanguage will be wired once LanguageSwitcher is implemented
  };

  const onLogout = async () => {
    await Promise.resolve(logout());
    if (typeof window !== 'undefined') {
      localStorage.removeItem('mks_forms_token');
      window.location.assign('/login');
    }
  };

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  const displayName = fullName || user?.email || t('header.guest', 'Usuário');
  const avatarSrc = user?.photo?.path ?? null;
  const themeOptions: Array<{ value: ThemeMode; label: string; Icon: LucideIcon }> = [
    { value: 'light', label: t('header.theme.light', 'Claro'), Icon: Sun },
    { value: 'dark', label: t('header.theme.dark', 'Escuro'), Icon: Moon },
    { value: 'system', label: t('header.theme.system', 'Sistema'), Icon: Monitor },
  ];

  const headerBtn =
    'inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-600 transition hover:bg-[#fcf0f6] hover:text-[#e20074] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#e20074] dark:text-slate-300 dark:hover:bg-[#3a0a23] dark:hover:text-[#ff70b3]';

  return (
    <header className="sticky top-0 z-40 flex h-14 items-center gap-2 border-b border-slate-200 bg-white/95 px-3 backdrop-blur dark:border-slate-700 dark:bg-slate-900/95 sm:gap-4 sm:px-5">
      {onToggleSidebar && (
        <button
          type="button"
          onClick={onToggleSidebar}
          aria-label={t('header.toggleSidebar', 'Alternar menu')}
          className={headerBtn}
        >
          <MenuIcon size={18} />
        </button>
      )}

      <Link
        to="/forms"
        className="flex items-center gap-3 rounded-md px-1 py-1 hover:opacity-90"
        aria-label="AIDA Forms"
      >
        <TSystemsMark variant="mark" size={28} />
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-semibold tracking-wide text-[#e20074]">AIDA</span>
          <span className="text-sm font-semibold tracking-wide text-slate-700 dark:text-slate-200">
            {t('brand', 'Forms')}
          </span>
        </div>
      </Link>

      <div className="flex-1" />

      {/* Fullscreen */}
      <button
        type="button"
        onClick={toggleFullscreen}
        aria-label={fullscreen ? t('header.exitFullscreen', 'Sair da tela cheia') : t('header.enterFullscreen', 'Tela cheia')}
        title={fullscreen ? t('header.exitFullscreen', 'Sair da tela cheia') : t('header.enterFullscreen', 'Tela cheia')}
        className={headerBtn}
      >
        {fullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
      </button>

      {/* Language */}
      <Menu as="div" className="relative">
        <Menu.Button aria-label={t('header.language', 'Idioma')} title={t('header.language', 'Idioma')} className={headerBtn}>
          <Globe size={18} />
        </Menu.Button>
        <Transition
          enter="transition ease-out duration-100"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 z-50 mt-2 w-44 origin-top-right overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg ring-1 ring-black/5 focus:outline-none dark:border-slate-700 dark:bg-slate-800">
            <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              {t('header.language', 'Idioma')}
            </div>
            {[
              { code: 'pt-BR', label: 'Português (BR)' },
              { code: 'en-US', label: 'English (US)' },
              { code: 'es-ES', label: 'Español' },
            ].map((opt) => (
              <Menu.Item key={opt.code}>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={() => onChangeLocale(opt.code)}
                    className={cn(
                      'flex w-full items-center justify-between gap-2 px-3 py-2 text-sm',
                      active ? 'bg-[#fcf0f6] text-[#e20074]' : 'text-slate-700 dark:text-slate-200',
                    )}
                  >
                    <span>{opt.label}</span>
                    {locale === opt.code && <Check size={14} />}
                  </button>
                )}
              </Menu.Item>
            ))}
          </Menu.Items>
        </Transition>
      </Menu>

      {/* Theme */}
      <Menu as="div" className="relative">
        <Menu.Button aria-label={t('header.theme.title', 'Tema')} title={t('header.theme.title', 'Tema')} className={headerBtn}>
          {theme === 'dark' ? <Moon size={18} /> : theme === 'light' ? <Sun size={18} /> : <Monitor size={18} />}
        </Menu.Button>
        <Transition
          enter="transition ease-out duration-100"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 z-50 mt-2 w-44 origin-top-right overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg ring-1 ring-black/5 focus:outline-none dark:border-slate-700 dark:bg-slate-800">
            <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              {t('header.theme.title', 'Tema')}
            </div>
            {themeOptions.map(({ value, label, Icon }) => (
              <Menu.Item key={value}>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={() => setTheme(value)}
                    className={cn(
                      'flex w-full items-center justify-between gap-2 px-3 py-2 text-sm',
                      active ? 'bg-[#fcf0f6] text-[#e20074]' : 'text-slate-700 dark:text-slate-200',
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <Icon size={14} />
                      {label}
                    </span>
                    {theme === value && <Check size={14} />}
                  </button>
                )}
              </Menu.Item>
            ))}
          </Menu.Items>
        </Transition>
      </Menu>

      {/* Notifications */}
      <Menu as="div" className="relative">
        <Menu.Button
          aria-label={t('header.notifications', 'Notificações')}
          title={t('header.notifications', 'Notificações')}
          className={headerBtn}
        >
          <Bell size={18} />
        </Menu.Button>
        <Transition
          enter="transition ease-out duration-100"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 z-50 mt-2 w-80 origin-top-right overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg ring-1 ring-black/5 focus:outline-none dark:border-slate-700 dark:bg-slate-800">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-700 dark:border-slate-700 dark:text-slate-200">
              {t('header.notifications', 'Notificações')}
              <span className="text-xs font-normal text-slate-400">
                {t('header.notificationsEmpty', 'Sem novidades')}
              </span>
            </div>
            <div className="flex flex-col items-center justify-center gap-1 px-4 py-6 text-center text-sm text-slate-500">
              <Bell size={22} className="text-slate-300" />
              {t('header.notificationsEmptyBody', 'Quando houver atualizações, elas aparecem aqui.')}
            </div>
          </Menu.Items>
        </Transition>
      </Menu>

      {/* User menu */}
      <Menu as="div" className="relative">
        <Menu.Button
          aria-label={t('header.userMenu', 'Conta')}
          title={t('header.userMenu', 'Conta')}
          className="inline-flex h-9 items-center gap-2 rounded-full px-1 py-1 pr-2 text-slate-700 transition hover:bg-[#fcf0f6] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#e20074] dark:text-slate-200 dark:hover:bg-[#3a0a23]"
        >
          <Avatar src={avatarSrc} fallback={displayName} size="sm" alt={displayName} />
          <span className="hidden max-w-[10rem] truncate text-sm font-medium md:inline">{displayName}</span>
          <ChevronDown size={14} className="hidden text-slate-400 md:inline" />
        </Menu.Button>
        <Transition
          enter="transition ease-out duration-100"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 z-50 mt-2 w-56 origin-top-right overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg ring-1 ring-black/5 focus:outline-none dark:border-slate-700 dark:bg-slate-800">
            <div className="border-b border-slate-100 px-4 py-3 text-sm dark:border-slate-700">
              <div className="truncate font-medium text-slate-800 dark:text-slate-100">
                {displayName}
              </div>
              {user?.email && (
                <div className="truncate text-xs text-slate-500">{user.email}</div>
              )}
            </div>
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => window.location.assign('/profile')}
                  className={cn(
                    'flex w-full items-center gap-2 px-4 py-2 text-sm',
                    active ? 'bg-[#fcf0f6] text-[#e20074]' : 'text-slate-700 dark:text-slate-200',
                  )}
                >
                  <UserIcon size={14} />
                  {t('header.profile', 'Perfil')}
                </button>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={onLogout}
                  className={cn(
                    'flex w-full items-center gap-2 border-t border-slate-100 px-4 py-2 text-sm dark:border-slate-700',
                    active ? 'bg-[#fcf0f6] text-[#e20074]' : 'text-slate-700 dark:text-slate-200',
                  )}
                >
                  <LogOut size={14} />
                  {t('header.logout', 'Sair')}
                </button>
              )}
            </Menu.Item>
          </Menu.Items>
        </Transition>
      </Menu>
    </header>
  );
};

export default Header;
